import { getCurrentUser } from "../auth";
import {
  getUserData,
  updateApiKeys,
  addSavedPrompt,
  removeSavedPrompt,
  SavedPrompt,
} from "../services/user-service";

export function renderDashboard(): string {
  return `
    <div class="dashboard-page">
      <div class="container">
        <div class="page-header">
          <h1>Dashboard</h1>
          <p class="page-subtitle" id="dashboard-email"></p>
        </div>

        <div class="dashboard-grid">
          <!-- API Keys Section -->
          <div class="card">
            <div class="card-header">
              <h2>API Keys</h2>
              <p class="card-description">Your API keys are synced with the Word add-in. Update them here or in the add-in.</p>
            </div>
            <div class="card-body">
              <div class="form-group">
                <label for="anthropic-key">Anthropic API Key (Claude)</label>
                <div class="key-input-group">
                  <input type="password" id="anthropic-key" placeholder="sk-ant-..." />
                  <button class="btn btn-sm btn-outline" id="toggle-anthropic" title="Show/hide key">Show</button>
                </div>
              </div>
              <div class="form-group">
                <label for="openai-key">OpenAI API Key (ChatGPT)</label>
                <div class="key-input-group">
                  <input type="password" id="openai-key" placeholder="sk-..." />
                  <button class="btn btn-sm btn-outline" id="toggle-openai" title="Show/hide key">Show</button>
                </div>
              </div>
              <div class="form-group">
                <label for="gemini-key">Google AI Studio API Key (Gemini)</label>
                <div class="key-input-group">
                  <input type="password" id="gemini-key" placeholder="AIza..." />
                  <button class="btn btn-sm btn-outline" id="toggle-gemini" title="Show/hide key">Show</button>
                </div>
              </div>
              <div id="keys-status" class="form-status" style="display:none;"></div>
              <button class="btn btn-primary" id="save-keys-btn">Save API Keys</button>
            </div>
          </div>

          <!-- Saved Prompts Section -->
          <div class="card">
            <div class="card-header">
              <h2>Saved Prompts</h2>
              <p class="card-description">Save your frequently used review instructions.</p>
            </div>
            <div class="card-body">
              <div id="prompts-list" class="prompts-list"></div>
              <div class="add-prompt-section">
                <div class="form-group">
                  <label for="prompt-name">Prompt Name</label>
                  <input type="text" id="prompt-name" placeholder="e.g., Formal Review" />
                </div>
                <div class="form-group">
                  <label for="prompt-text">Prompt Text</label>
                  <textarea id="prompt-text" rows="3" placeholder="e.g., Review for formal business tone..."></textarea>
                </div>
                <button class="btn btn-outline" id="add-prompt-btn">Add Prompt</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function mountDashboard(): void {
  const user = getCurrentUser();
  if (!user) return;

  const emailEl = document.getElementById("dashboard-email")!;
  emailEl.textContent = user.email || "";

  loadDashboardData().catch((err) => {
    console.error("Failed to load dashboard data:", err);
  });

  // API Key toggles
  setupKeyToggle("anthropic");
  setupKeyToggle("openai");
  setupKeyToggle("gemini");

  // Save keys
  document.getElementById("save-keys-btn")!.addEventListener("click", saveKeys);

  // Add prompt
  document.getElementById("add-prompt-btn")!.addEventListener("click", handleAddPrompt);
}

async function loadDashboardData(): Promise<void> {
  const data = await getUserData();
  if (!data) return;

  // Populate API keys
  const anthropicInput = document.getElementById("anthropic-key") as HTMLInputElement;
  const openaiInput = document.getElementById("openai-key") as HTMLInputElement;
  const geminiInput = document.getElementById("gemini-key") as HTMLInputElement;

  if (data.apiKeys?.anthropic) anthropicInput.value = data.apiKeys.anthropic;
  if (data.apiKeys?.openai) openaiInput.value = data.apiKeys.openai;
  if (data.apiKeys?.gemini) geminiInput.value = data.apiKeys.gemini;

  // Saved prompts
  renderPromptsList(data.savedPrompts || []);
}

function setupKeyToggle(provider: string): void {
  const btn = document.getElementById(`toggle-${provider}`)!;
  const input = document.getElementById(`${provider}-key`) as HTMLInputElement;

  btn.addEventListener("click", () => {
    if (input.type === "password") {
      input.type = "text";
      btn.textContent = "Hide";
    } else {
      input.type = "password";
      btn.textContent = "Show";
    }
  });
}

async function saveKeys(): Promise<void> {
  const anthropic = (document.getElementById("anthropic-key") as HTMLInputElement).value.trim() || null;
  const openai = (document.getElementById("openai-key") as HTMLInputElement).value.trim() || null;
  const gemini = (document.getElementById("gemini-key") as HTMLInputElement).value.trim() || null;
  const statusEl = document.getElementById("keys-status")!;
  const btn = document.getElementById("save-keys-btn") as HTMLButtonElement;

  btn.disabled = true;
  btn.textContent = "Saving...";

  try {
    await updateApiKeys({ anthropic, openai, gemini });
    statusEl.className = "form-status form-success";
    statusEl.textContent = "API keys saved successfully!";
    statusEl.style.display = "block";
  } catch (err: any) {
    statusEl.className = "form-status form-error";
    statusEl.textContent = "Failed to save: " + err.message;
    statusEl.style.display = "block";
  } finally {
    btn.disabled = false;
    btn.textContent = "Save API Keys";
    setTimeout(() => {
      statusEl.style.display = "none";
    }, 3000);
  }
}

function renderPromptsList(prompts: SavedPrompt[]): void {
  const list = document.getElementById("prompts-list")!;

  if (prompts.length === 0) {
    list.innerHTML = '<p class="empty-state">No saved prompts yet.</p>';
    return;
  }

  list.innerHTML = prompts
    .map(
      (p) => `
    <div class="prompt-item" data-id="${p.id}">
      <div class="prompt-info">
        <strong>${escapeHtml(p.name)}</strong>
        <p>${escapeHtml(p.prompt)}</p>
      </div>
      <button class="btn btn-sm btn-danger delete-prompt-btn" data-id="${p.id}">Delete</button>
    </div>
  `
    )
    .join("");

  // Attach delete handlers
  list.querySelectorAll(".delete-prompt-btn").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const id = (btn as HTMLElement).dataset.id!;
      const prompt = prompts.find((p) => p.id === id);
      if (prompt) {
        await removeSavedPrompt(prompt);
        loadDashboardData();
      }
    });
  });
}

async function handleAddPrompt(): Promise<void> {
  const nameInput = document.getElementById("prompt-name") as HTMLInputElement;
  const textInput = document.getElementById("prompt-text") as HTMLTextAreaElement;
  const name = nameInput.value.trim();
  const prompt = textInput.value.trim();

  if (!name || !prompt) return;

  await addSavedPrompt(name, prompt);
  nameInput.value = "";
  textInput.value = "";
  loadDashboardData();
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
